<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة عن المقرأة</h1>
        <p class="mt-2 text-sm text-gray-700">تعديل بيانات الرؤية والرسالة والهدف للفرع</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="mt-8 text-center py-12">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p class="mt-2 text-gray-600">جاري التحميل...</p>
    </div>

    <!-- Form -->
    <div v-else class="mt-8 bg-white shadow rounded-lg p-6">
      <form @submit.prevent="handleSave">
        <div class="space-y-6">
          <!-- Vision -->
          <div>
            <label for="vision" class="block text-sm font-medium text-gray-700 mb-2">
              الرؤية <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="formData.vision"
              id="vision"
              rows="3"
              required
              placeholder="مثال: نحو مقرأة قرآنيّة محفِّزة"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            ></textarea>
            <p class="mt-1 text-xs text-gray-500">الحد الأقصى: 500 حرف</p>
          </div>

          <!-- Mission -->
          <div>
            <label for="mission" class="block text-sm font-medium text-gray-700 mb-2">
              الرسالة <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="formData.mission"
              id="mission"
              rows="4"
              required
              placeholder="مثال: نسعى إلى الإبداع والتميز في تعليم وخدمة حَمَلة القرآن الكريم عبر أحدث الوسائل الرقمية"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            ></textarea>
            <p class="mt-1 text-xs text-gray-500">الحد الأقصى: 1000 حرف</p>
          </div>

          <!-- Goal -->
          <div>
            <label for="goal" class="block text-sm font-medium text-gray-700 mb-2">
              الهدف <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="formData.goal"
              id="goal"
              rows="3"
              required
              placeholder="مثال: أن يسمع الدارس القرآن كاملاً عن ظهر قلب في يوم واحد بجودة وإتقان"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            ></textarea>
            <p class="mt-1 text-xs text-gray-500">الحد الأقصى: 500 حرف</p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-8 flex justify-end gap-3">
          <button
            type="button"
            @click="handleReset"
            class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            إعادة تعيين
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="px-4 py-2 bg-customHafadhColor text-white rounded-md shadow-sm text-sm font-medium hover:bg-customHafadhColor/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
            {{ saving ? 'جاري الحفظ...' : 'حفظ التغييرات' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import url from '@/url.js'

export default defineComponent({
  name: 'AboutUsManagement',
  data() {
    return {
      loading: false,
      saving: false,
      formData: {
        vision: '',
        mission: '',
        goal: ''
      },
      originalData: {
        vision: '',
        mission: '',
        goal: ''
      },
      alert: {
        show: false,
        type: 'success',
        message: ''
      }
    }
  },
  mounted() {
    this.loadTenantData()
  },
  methods: {
    async loadTenantData() {
      this.loading = true
      try {
        // Get tenant_id from JWT token
        let tenantId = null
        try {
          const token = localStorage.getItem('token')
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]))
            tenantId = payload.tenantId || payload.tenant_id || null
          }
        } catch (e) {
          console.warn('Could not extract tenantId from token:', e)
          this.showAlert('danger', 'فشل في تحديد الفرع')
          this.loading = false
          return
        }

        if (!tenantId) {
          this.showAlert('danger', 'لم يتم العثور على معرف الفرع')
          this.loading = false
          return
        }

        // Fetch tenant data
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.tenants_show + tenantId,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        // Extract tenant data from response
        let tenant = null
        if (response.data?.result?.data) {
          tenant = response.data.result.data
        } else if (response.data?.data) {
          tenant = response.data.data
        } else if (response.data?.result) {
          tenant = response.data.result
        } else {
          tenant = response.data
        }

        if (tenant) {
          this.formData = {
            vision: tenant.vision || '',
            mission: tenant.mission || '',
            goal: tenant.goal || ''
          }
          this.originalData = { ...this.formData }
        }
      } catch (error) {
        console.error('Failed to load tenant data', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في تحميل بيانات الفرع')
      } finally {
        this.loading = false
      }
    },
    async handleSave() {
      // Validate
      if (!this.formData.vision || !this.formData.mission || !this.formData.goal) {
        this.showAlert('danger', 'يرجى ملء جميع الحقول المطلوبة')
        return
      }

      if (this.formData.vision.length > 500) {
        this.showAlert('danger', 'الرؤية يجب أن تكون أقل من 500 حرف')
        return
      }

      if (this.formData.mission.length > 1000) {
        this.showAlert('danger', 'الرسالة يجب أن تكون أقل من 1000 حرف')
        return
      }

      if (this.formData.goal.length > 500) {
        this.showAlert('danger', 'الهدف يجب أن يكون أقل من 500 حرف')
        return
      }

      this.saving = true
      try {
        // Get tenant_id from JWT token
        let tenantId = null
        try {
          const token = localStorage.getItem('token')
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]))
            tenantId = payload.tenantId || payload.tenant_id || null
          }
        } catch (e) {
          console.warn('Could not extract tenantId from token:', e)
          this.showAlert('danger', 'فشل في تحديد الفرع')
          this.saving = false
          return
        }

        if (!tenantId) {
          this.showAlert('danger', 'لم يتم العثور على معرف الفرع')
          this.saving = false
          return
        }

        // Use FormData to send data
        const formData = new FormData()
        formData.append('vision', this.formData.vision)
        formData.append('mission', this.formData.mission)
        formData.append('goal', this.formData.goal)
        formData.append('_method', 'PUT')

        // Update tenant
        await axios.post(
          process.env.VUE_APP_SERVICE_URL + url.tenants_update + tenantId,
          formData,
          {
            headers: {
              'X-Authorization': localStorage.getItem('token')
            },
            transformRequest: [(data) => {
              return data
            }]
          }
        )

        this.originalData = { ...this.formData }
        this.showAlert('success', 'تم حفظ التغييرات بنجاح')
      } catch (error) {
        console.error('Failed to save tenant data', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في حفظ التغييرات')
      } finally {
        this.saving = false
      }
    },
    handleReset() {
      this.formData = { ...this.originalData }
      this.showAlert('info', 'تم إعادة تعيين النموذج')
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    }
  }
})
</script>

<style scoped>
.enabled-button {
  @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors;
}
</style>

